<template>
  <div>
    <h5 class="mb-3 font-weight-bold text-dark">🔑 API 权限管理</h5>

    <b-tabs v-model="activeTab" pills card class="admin-tabs">

      <!-- ═══════════════════════════════════════════════════════════════
           Tab 1: 用户权益管理
      ═══════════════════════════════════════════════════════════════ -->
      <b-tab title="用户权益" active>
        <!-- 用户搜索 -->
        <b-card class="mb-3 shadow-sm">
          <div class="d-flex align-items-center">
            <b-form-input
              v-model="searchId"
              placeholder="输入用户 ID 或用户名"
              size="sm"
              style="max-width: 260px;"
              @keyup.enter="searchUser"
            />
            <b-button variant="primary" size="sm" class="ml-2" @click="searchUser" :disabled="loadingUser">
              {{ loadingUser ? '搜索中...' : '查询' }}
            </b-button>
          </div>
        </b-card>

        <!-- 查询结果 -->
        <div v-if="targetUser">
          <!-- 用户基础信息 -->
          <b-card class="mb-3 shadow-sm">
            <template #header>
              <span class="font-weight-bold">👤 用户信息</span>
            </template>
            <b-row>
              <b-col sm="3"><span class="text-muted">ID：</span><b>{{ targetUser.user_id }}</b></b-col>
              <b-col sm="3"><span class="text-muted">用户名：</span><b>{{ targetUser.user_name }}</b></b-col>
              <b-col sm="3"><span class="text-muted">昵称：</span><b>{{ targetUser.user_nick || '-' }}</b></b-col>
              <b-col sm="3"><span class="text-muted">角色：</span><b-badge variant="info">{{ targetUser.roles }}</b-badge></b-col>
            </b-row>
          </b-card>

          <!-- 积分管理 -->
          <b-card class="mb-3 shadow-sm">
            <template #header>
              <span class="font-weight-bold">💰 积分余额</span>
              <span class="ml-2 badge badge-success" style="font-size: 1rem;">{{ userPoints }} 分</span>
            </template>
            <b-button-group size="sm">
              <b-button variant="success" @click="openPointsModal('add')">➕ 发放积分</b-button>
              <b-button variant="warning" @click="openPointsModal('sub')">➖ 扣除积分</b-button>
            </b-button-group>
          </b-card>

          <!-- 订阅管理 -->
          <b-card class="shadow-sm">
            <template #header>
              <span class="font-weight-bold">📋 有效订阅</span>
              <b-button size="sm" variant="primary" class="float-right" @click="openGrantModal">＋ 新增订阅</b-button>
            </template>
            <b-table
              :items="userSubscriptions"
              :fields="subFields"
              small
              hover
              responsive
              show-empty
              empty-text="暂无有效订阅"
            >
              <template #cell(end_date)="row">
                <span :class="isExpiringSoon(row.item.end_date) ? 'text-warning' : ''">
                  {{ row.item.end_date }}
                </span>
              </template>
            </b-table>
          </b-card>
        </div>

        <b-alert v-else-if="searchId && !loadingUser" show variant="secondary" class="mt-3">
          请输入用户 ID 或用户名后点击查询
        </b-alert>
      </b-tab>

      <!-- ═══════════════════════════════════════════════════════════════
           Tab 2: 规则配置
      ═══════════════════════════════════════════════════════════════ -->
      <b-tab title="规则配置">
        <!-- 积分档位规则 -->
        <b-card class="mb-4 shadow-sm">
          <template #header>
            <span class="font-weight-bold">📊 积分档位规则</span>
            <b-button size="sm" variant="success" class="float-right" @click="openPointsRateModal(null)">＋ 新增</b-button>
          </template>
          <b-table
            :items="pointsRateRules"
            :fields="pointsRateFields"
            small
            hover
            responsive
            show-empty
            empty-text="暂无规则"
          >
            <template #cell(actions)="row">
              <b-button-group size="sm">
                <b-button variant="outline-primary" @click="openPointsRateModal(row.item)">编辑</b-button>
                <b-button variant="outline-danger" @click="deletePointsRate(row.item.id)">删除</b-button>
              </b-button-group>
            </template>
          </b-table>
        </b-card>

        <!-- 套餐模板 -->
        <b-card class="shadow-sm">
          <template #header>
            <span class="font-weight-bold">🎁 订阅套餐模板</span>
            <b-button size="sm" variant="success" class="float-right" @click="openPlanModal(null)">＋ 新增</b-button>
          </template>
          <b-table
            :items="subscriptionPlans"
            :fields="planFields"
            small
            hover
            responsive
            show-empty
            empty-text="暂无套餐"
          >
            <template #cell(status)="row">
              <b-badge :variant="row.item.status === 1 ? 'success' : 'secondary'">
                {{ row.item.status === 1 ? '启用' : '禁用' }}
              </b-badge>
            </template>
            <template #cell(actions)="row">
              <b-button size="sm" variant="outline-primary" @click="openPlanModal(row.item)">编辑</b-button>
            </template>
          </b-table>
        </b-card>
      </b-tab>
    </b-tabs>

    <!-- ── 弹窗：调整积分 ── -->
    <b-modal v-model="showPointsModal" :title="pointsAction === 'add' ? '➕ 发放积分' : '➖ 扣除积分'" @ok="submitPoints" ok-title="确认" cancel-title="取消">
      <b-form-group label="调整数量" description="请输入正整数">
        <b-form-input v-model.number="pointsAmount" type="number" min="1" />
      </b-form-group>
      <b-form-group label="备注（可选）">
        <b-form-input v-model="pointsRemark" placeholder="操作原因" />
      </b-form-group>
    </b-modal>

    <!-- ── 弹窗：分配订阅 ── -->
    <b-modal v-model="showGrantModal" title="📋 分配订阅套餐" @ok="submitGrant" ok-title="确认" cancel-title="取消">
      <b-form-group label="选择套餐">
        <b-form-select v-model="grantPlanId" :options="planOptions" />
      </b-form-group>
      <b-form-group label="开始日期">
        <b-form-input v-model="grantStartDate" type="date" />
      </b-form-group>
      <b-form-group label="结束日期">
        <b-form-input v-model="grantEndDate" type="date" />
      </b-form-group>
    </b-modal>

    <!-- ── 弹窗：积分规则编辑 ── -->
    <b-modal v-model="showPointsRateModal" :title="editingRule ? '编辑积分规则' : '新增积分规则'" @ok="submitPointsRate" ok-title="保存" cancel-title="取消">
      <b-form-group label="接口标识 (endpoint)">
        <b-form-input v-model="ruleForm.endpoint" placeholder="如 rt_k / stk_mins" />
      </b-form-group>
      <b-form-group label="最低积分要求 (min_points)">
        <b-form-input v-model.number="ruleForm.min_points" type="number" />
      </b-form-group>
      <b-form-group label="限频次数/分钟 (limit_per_min)">
        <b-form-input v-model.number="ruleForm.limit_per_min" type="number" />
      </b-form-group>
      <b-form-group label="备注">
        <b-form-input v-model="ruleForm.description" />
      </b-form-group>
    </b-modal>

    <!-- ── 弹窗：套餐模板编辑 ── -->
    <b-modal v-model="showPlanModal" :title="editingPlan ? '编辑套餐' : '新增套餐'" @ok="submitPlan" ok-title="保存" cancel-title="取消">
      <b-form-group label="套餐名称"><b-form-input v-model="planForm.name" /></b-form-group>
      <b-form-group label="接口标识 (endpoint，* 表示全部)"><b-form-input v-model="planForm.endpoint" /></b-form-group>
      <b-form-group label="类型">
        <b-form-select v-model="planForm.plan_type" :options="[{value:'monthly',text:'月度'},{value:'yearly',text:'年度'}]" />
      </b-form-group>
      <b-form-group label="限频次数/分钟"><b-form-input v-model.number="planForm.limit_per_min" type="number" /></b-form-group>
      <b-form-group label="价格（元，可为空）"><b-form-input v-model.number="planForm.price" type="number" /></b-form-group>
      <b-form-group label="描述"><b-form-input v-model="planForm.description" /></b-form-group>
      <b-form-group label="状态">
        <b-form-select v-model="planForm.status" :options="[{value:1,text:'启用'},{value:0,text:'禁用'}]" />
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import { request_zzquant } from '@/network/request'

const API = url => `/v3/admin${url}`

export default {
  name: 'AdminApiAuth',
  data() {
    return {
      activeTab: 0,

      // -- Tab1: 用户权益 --
      searchId: '',
      loadingUser: false,
      targetUser: null,
      userPoints: 0,
      userSubscriptions: [],
      subFields: [
        { key: 'plan_name', label: '套餐名称' },
        { key: 'endpoint', label: '接口' },
        { key: 'limit_per_min', label: '限频/min' },
        { key: 'start_date', label: '开始日期' },
        { key: 'end_date', label: '结束日期' },
      ],

      // 积分弹窗
      showPointsModal: false,
      pointsAction: 'add',
      pointsAmount: 100,
      pointsRemark: '',

      // 分配订阅弹窗
      showGrantModal: false,
      grantPlanId: null,
      grantStartDate: '',
      grantEndDate: '',
      planOptions: [],

      // -- Tab2: 规则配置 --
      pointsRateRules: [],
      pointsRateFields: [
        { key: 'endpoint', label: '接口标识' },
        { key: 'min_points', label: '最低积分' },
        { key: 'limit_per_min', label: '限频/min' },
        { key: 'description', label: '备注' },
        { key: 'actions', label: '操作' },
      ],
      subscriptionPlans: [],
      planFields: [
        { key: 'name', label: '套餐名称' },
        { key: 'endpoint', label: '接口' },
        { key: 'plan_type', label: '类型' },
        { key: 'limit_per_min', label: '限频/min' },
        { key: 'price', label: '价格(元)' },
        { key: 'status', label: '状态' },
        { key: 'actions', label: '操作' },
      ],

      // 积分规则弹窗
      showPointsRateModal: false,
      editingRule: null,
      ruleForm: { endpoint: '', min_points: 0, limit_per_min: 60, description: '' },

      // 套餐弹窗
      showPlanModal: false,
      editingPlan: null,
      planForm: { name: '', endpoint: '', plan_type: 'monthly', limit_per_min: 120, price: null, description: '', status: 1 },
    }
  },
  created() {
    this.loadRules()
    this.loadPlans()
  },
  methods: {
    // ── 用户搜索 ──────────────────────────────────────────────────────
    async searchUser() {
      if (!this.searchId) return
      this.loadingUser = true
      this.targetUser = null
      try {
        const res = await request_zzquant({ url: API(`/user/${this.searchId}`), method: 'get' })
        const d = res.data && res.data.data ? res.data.data : null
        if (!d) throw new Error('用户不存在')
        this.targetUser = d.user
        this.userPoints = d.points
        this.userSubscriptions = d.subscriptions
      } catch (e) {
        this.$bvToast.toast(e.message || '查询失败', { variant: 'danger', solid: true, title: '错误' })
      } finally {
        this.loadingUser = false
      }
    },

    isExpiringSoon(endDate) {
      if (!endDate) return false
      const diff = (new Date(endDate) - new Date()) / 86400000
      return diff >= 0 && diff <= 7
    },

    // ── 积分操作 ──────────────────────────────────────────────────────
    openPointsModal(action) {
      this.pointsAction = action
      this.pointsAmount = 100
      this.pointsRemark = ''
      this.showPointsModal = true
    },
    async submitPoints(bvModalEvt) {
      bvModalEvt.preventDefault()
      try {
        await request_zzquant({
          url: API('/points/adjust'),
          method: 'post',
          data: {
            user_id: this.targetUser.user_id,
            amount: this.pointsAmount,
            action: this.pointsAction,
            remark: this.pointsRemark || undefined,
          }
        })
        this.$bvToast.toast('积分调整成功', { variant: 'success', solid: true, title: '成功' })
        this.showPointsModal = false
        this.searchUser()
      } catch (e) {
        this.$bvToast.toast(e.message || '操作失败', { variant: 'danger', solid: true, title: '错误' })
      }
    },

    // ── 分配订阅 ──────────────────────────────────────────────────────
    async openGrantModal() {
      await this.loadAllPlansForSelect()
      const today = new Date().toISOString().slice(0, 10)
      this.grantStartDate = today
      const nextMonth = new Date(); nextMonth.setMonth(nextMonth.getMonth() + 1)
      this.grantEndDate = nextMonth.toISOString().slice(0, 10)
      this.grantPlanId = this.planOptions.length ? this.planOptions[0].value : null
      this.showGrantModal = true
    },
    async loadAllPlansForSelect() {
      try {
        const res = await request_zzquant({ url: API('/plans'), method: 'get' })
        const plans = (res.data && res.data.data && res.data.data.plans) || []
        this.planOptions = plans.map(p => ({ value: p.id, text: `${p.name}（${p.endpoint}，${p.limit_per_min}/min）` }))
      } catch (e) { /* ignore */ }
    },
    async submitGrant(bvModalEvt) {
      bvModalEvt.preventDefault()
      try {
        const res = await request_zzquant({
          url: API('/subscription/grant'),
          method: 'post',
          data: {
            user_id: this.targetUser.user_id,
            plan_id: this.grantPlanId,
            start_date: this.grantStartDate,
            end_date: this.grantEndDate,
          }
        })
        const msg = (res.data && res.data.message) || '分配成功'
        this.$bvToast.toast(msg, { variant: 'success', solid: true, title: '成功' })
        this.showGrantModal = false
        this.searchUser()
      } catch (e) {
        this.$bvToast.toast(e.message || '操作失败', { variant: 'danger', solid: true, title: '错误' })
      }
    },

    // ── 积分规则 CRUD ─────────────────────────────────────────────────
    async loadRules() {
      try {
        const res = await request_zzquant({ url: '/v3/admin/rules/points_rate', method: 'get' })
        this.pointsRateRules = (res.data && res.data.data && res.data.data.rules) || []
      } catch (e) { /* ignore */ }
    },
    openPointsRateModal(rule) {
      this.editingRule = rule
      this.ruleForm = rule
        ? { endpoint: rule.endpoint, min_points: rule.min_points, limit_per_min: rule.limit_per_min, description: rule.description || '' }
        : { endpoint: '', min_points: 0, limit_per_min: 60, description: '' }
      this.showPointsRateModal = true
    },
    async submitPointsRate(bvModalEvt) {
      bvModalEvt.preventDefault()
      try {
        if (this.editingRule) {
          await request_zzquant({ url: `/v3/admin/rules/points_rate/${this.editingRule.id}`, method: 'put', data: this.ruleForm })
        } else {
          await request_zzquant({ url: '/v3/admin/rules/points_rate', method: 'post', data: this.ruleForm })
        }
        this.$bvToast.toast('保存成功', { variant: 'success', solid: true, title: '成功' })
        this.showPointsRateModal = false
        this.loadRules()
      } catch (e) {
        this.$bvToast.toast(e.message || '保存失败', { variant: 'danger', solid: true, title: '错误' })
      }
    },
    async deletePointsRate(id) {
      const ok = await this.$bvModal.msgBoxConfirm('确定删除该规则吗？删除后将在5分钟内自动生效。', { title: '确认删除', okVariant: 'danger', okTitle: '删除', cancelTitle: '取消' })
      if (!ok) return
      try {
        await request_zzquant({ url: `/v3/admin/rules/points_rate/${id}`, method: 'delete' })
        this.$bvToast.toast('删除成功', { variant: 'success', solid: true, title: '成功' })
        this.loadRules()
      } catch (e) {
        this.$bvToast.toast(e.message || '删除失败', { variant: 'danger', solid: true, title: '错误' })
      }
    },

    // ── 套餐模板 CRUD ─────────────────────────────────────────────────
    async loadPlans() {
      try {
        const res = await request_zzquant({ url: '/v3/admin/rules/subscription_plan', method: 'get' })
        this.subscriptionPlans = (res.data && res.data.data && res.data.data.plans) || []
      } catch (e) { /* ignore */ }
    },
    openPlanModal(plan) {
      this.editingPlan = plan
      this.planForm = plan
        ? { name: plan.name, endpoint: plan.endpoint, plan_type: plan.plan_type, limit_per_min: plan.limit_per_min, price: plan.price, description: plan.description || '', status: plan.status }
        : { name: '', endpoint: '', plan_type: 'monthly', limit_per_min: 120, price: null, description: '', status: 1 }
      this.showPlanModal = true
    },
    async submitPlan(bvModalEvt) {
      bvModalEvt.preventDefault()
      try {
        if (this.editingPlan) {
          await request_zzquant({ url: `/v3/admin/rules/subscription_plan/${this.editingPlan.id}`, method: 'put', data: this.planForm })
        } else {
          await request_zzquant({ url: '/v3/admin/rules/subscription_plan', method: 'post', data: this.planForm })
        }
        this.$bvToast.toast('保存成功', { variant: 'success', solid: true, title: '成功' })
        this.showPlanModal = false
        this.loadPlans()
      } catch (e) {
        this.$bvToast.toast(e.message || '保存失败', { variant: 'danger', solid: true, title: '错误' })
      }
    },
  }
}
</script>

<style scoped>
.admin-tabs >>> .nav-pills .nav-link {
  color: #495057;
}
.admin-tabs >>> .nav-pills .nav-link.active {
  background-color: #2563eb;
}
</style>
